/**
 * The AI module's live data for the settings section: state.ext.ai from the store, kept live
 * by SSE. Until the store has it (an older hub), it reads GET /api/ai/state every few seconds
 * while the page is visible. Demo mode uses fixtures.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AiConnectInfo, AiState } from '@orgus/protocol';
import { apiGet } from '../../lib/api.ts';
import { useOrgus, useExt } from '../../lib/store.ts';
import { mockAiState, mockConnectInfo } from './mock.ts';

export const POLL_MS = 5000;

/** ready with `stale`: the last refresh failed, so this is what the hub said at `at`. */
export type Load<T> = { status: 'loading' } | { status: 'error'; error: unknown } | { status: 'ready'; data: T; at: number; stale?: boolean };

/** state.ext.ai from the store (kept live by SSE), when the hub has the ai module. */
function useStoreExt(): AiState | undefined {
  return useExt<AiState>('ai');
}

export function useAiState(): { load: Load<AiState>; retry: () => Promise<void>; live: boolean } {
  const mode = useOrgus((s) => s.mode);
  const fromStore = useStoreExt();
  const [load, setLoad] = useState<Load<AiState>>({ status: 'loading' });
  const alive = useRef(true);
  const mock = useMemo(() => (mode === 'mock' ? mockAiState() : null), [mode]);

  const fetchOnce = useCallback(async () => {
    try {
      const data = await apiGet<AiState>('/api/ai/state');
      if (alive.current) setLoad({ status: 'ready', data, at: Date.now() });
    } catch (error) {
      // Keep showing the last good data; only an empty view turns into an error.
      if (alive.current) setLoad((prev) => (prev.status === 'ready' ? { ...prev, stale: true } : { status: 'error', error }));
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (mode === 'mock' || fromStore) return () => void (alive.current = false);
    void fetchOnce();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void fetchOnce();
    }, POLL_MS);
    return () => {
      alive.current = false;
      clearInterval(t);
    };
  }, [mode, fromStore, fetchOnce]);

  const retry = useCallback(async () => {
    setLoad({ status: 'loading' });
    await fetchOnce();
  }, [fetchOnce]);

  if (mock) return { load: { status: 'ready', data: mock, at: Date.now() }, retry, live: true };
  if (fromStore) return { load: { status: 'ready', data: fromStore, at: Date.now() }, retry, live: true };
  return { load, retry, live: load.status === 'ready' && !load.stale };
}

export function useConnectInfo(): { load: Load<AiConnectInfo>; retry: () => Promise<void> } {
  const mode = useOrgus((s) => s.mode);
  const [load, setLoad] = useState<Load<AiConnectInfo>>({ status: 'loading' });
  const mock = useMemo(() => (mode === 'mock' ? mockConnectInfo() : null), [mode]);

  const fetchOnce = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      setLoad({ status: 'ready', data: await apiGet<AiConnectInfo>('/api/ai/connect'), at: Date.now() });
    } catch (error) {
      setLoad({ status: 'error', error });
    }
  }, []);

  useEffect(() => {
    if (mode !== 'mock') void fetchOnce();
  }, [mode, fetchOnce]);

  if (mock) return { load: { status: 'ready', data: mock, at: 0 }, retry: fetchOnce };
  return { load, retry: fetchOnce };
}
